import "./users.css";

import { Button, Table } from "reactstrap";
import {
  DEFAULT_USERS_SORT,
  USERS_PER_PAGE,
  changeAccess,
  deleteUser,
  fetchUsers,
  resetUserAlert,
  updateUserInformation,
} from "../actions/users";
import React, { Component } from "react";

import { EmptyState } from "../components/EmptyState";
import { LevelHeader } from "../clientDashboard/components/LevelHeader";
import PropTypes from "prop-types";
import { RefreshButton } from "../components/dataTable";
import RoleForm from "./roleForm";
import { CfSelect as Select } from "../components/CfSelect";
import UserDrawer from "./UserDrawer";
import _ from "lodash";
import { connect } from "react-redux";
import { fetchCompanies } from "../actions/companies";

// No "All" option: the placeholder stands for "no filter" and clearing the select
// sends the empty value, as the removed <option value=""> did.
const ACCESS_OPTIONS = [
  { value: "true", label: "Has access" },
  { value: "false", label: "Pending access" },
];

// Server-side sort: values must match UserListing::SORTABLE_COLUMNS on the backend.
const SORT_OPTIONS = [
  { value: "company", label: "Sort: Company" },
  { value: "-created_at", label: "Sort: Newest" },
  { value: "email", label: "Sort: Email" },
];

class Users extends Component {
  constructor(props) {
    super(props);
    this.state = {
      updatedUser: {},
      isOpenedModal: false,
      search: "",
    };
    this.handleCreateUser = this.handleCreateUser.bind(this);
    // Server-side search: debounce keystrokes and always reset to the first page.
    this.debouncedSearch = _.debounce((q) => {
      this.props.handleRefresh({ page: 1, q });
    }, 800);
  }

  handleFilterChange = (event) => {
    const search = event.target.value;
    this.setState({ search });
    this.debouncedSearch(search);
  };

  goToPage = (page) => {
    this.props.handleRefresh({ page });
  };

  // Clicking a row opens the user page, except when the click comes from an
  // interactive control inside the row (toggle, role select, action buttons).
  handleRowClick = (event, user) => {
    if (event.target.closest("button, a, input, select, label")) return;
    this.props.history.push(`/users/${user.id}`);
  };

  handleFilterSelect = (key, value) => {
    const filters = { ...this.props.filters, [key]: value };
    this.props.handleRefresh({ page: 1, filters });
  };

  // Clearing the select (option is null) goes back to the default name sort.
  handleSortChange = (option) => {
    this.props.handleRefresh({
      page: 1,
      sort: option?.value ?? DEFAULT_USERS_SORT,
    });
  };

  componentDidMount() {
    // The store may still hold the sort of a previous visit: start again from the default.
    this.props.handleRefresh({ page: 1, q: "", sort: DEFAULT_USERS_SORT });
    this.props.fetchCompanies();
  }

  componentWillUnmount() {
    this.debouncedSearch.cancel();
  }

  resetState() {
    this.setState({
      updatedUser: {},
      isOpenedModal: false,
    });
  }

  handleChangeAccess(event, user) {
    event.preventDefault();
    const granting = event.target.checked;
    if (granting && !user.company) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        `"${user.fullname}" has no company. Grant access anyway?`
      );
      if (!confirmed) return;
    }
    this.props.changeAccess(user.id, granting);
  }

  handleCreateUser(event) {
    const user = {
      id: null,
      lastname: "",
      firstname: "",
      phone: "",
      email: "",
      company_id: null,
    };
    this.handleUpdateUser(event, user);
  }

  handleUpdateUser(event, user) {
    event.preventDefault();
    this.setState({
      updatedUser: user,
      isOpenedModal: true,
    });
  }

  handleDeleteUser(event, user) {
    event.preventDefault();
    // eslint-disable-next-line no-alert
    const confirmation = window.confirm(
      `Are you sure you want to permanently delete "${user.fullname}"?`
    );
    if (confirmation) this.props.onDeleteUser(user);
  }

  cancelModal() {
    this.props.resetUserAlert();
    this.resetState();
  }

  renderUserRow(user) {
    const isSelf = this.props.currentUser.self.id === user.id;
    const name =
      user.fullname || `${user.firstname || ""} ${user.lastname || ""}`.trim();
    const meta = [user.email, user.phone].filter(Boolean).join(" · ");

    return (
      <tr
        key={user.id}
        className={isSelf ? "users-row users-row--self" : "users-row"}
        onClick={(event) => this.handleRowClick(event, user)}
      >
        <td className="users-cell-identity">
          <div className="users-identity">
            <span className="users-identity__name">
              {name}
              {isSelf && (
                <span className="cf-badge cf-badge--ok ms-2">You</span>
              )}
            </span>
            {meta && <span className="users-identity__meta">{meta}</span>}
          </div>
        </td>
        <td className={user.company ? "" : "users-cell--empty"}>
          {user.company ? user.company.name : "—"}
        </td>
        <td className="text-center">
          <RoleForm userDetails={user} />
        </td>
        <td className="text-center">
          <label
            className="users-access form-check form-switch"
            {...(isSelf
              ? {
                  "data-tooltip-id": "tooltip",
                  "data-tooltip-content": "You cannot change your own access",
                }
              : {})}
          >
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              onChange={(event) => this.handleChangeAccess(event, user)}
              disabled={isSelf}
              checked={user.access}
            />
            <span
              className={
                "cf-badge " + (user.access ? "cf-badge--ok" : "cf-badge--warn")
              }
            >
              {user.access ? "Active" : "Pending"}
            </span>
          </label>
        </td>
        <td className="users-cell-actions">
          <Button
            data-tooltip-id="tooltip"
            data-tooltip-content="Edit user information"
            className="p-0"
            color="link"
            onClick={(event) => this.handleUpdateUser(event, user)}
          >
            <i className="fa-solid fa-lg fa-pencil" />
          </Button>
          <Button
            data-tooltip-id="tooltip"
            data-tooltip-content="Assign campaigns"
            className="ms-2 p-0"
            color="link"
            onClick={() => this.props.history.push(`/users/${user.id}`)}
          >
            <i className="fa-solid fa-file-signature" />
          </Button>
          <Button
            data-tooltip-id="tooltip"
            data-tooltip-content="Delete user"
            data-tooltip-variant="error"
            className="ms-2 p-0"
            color="link danger"
            disabled={isSelf}
            onClick={(event) => this.handleDeleteUser(event, user)}
          >
            <i className="fa-solid fa-lg fa-trash-can" />
          </Button>
        </td>
      </tr>
    );
  }

  renderTableHeader() {
    return (
      <thead>
        <tr>
          <th>User</th>
          <th>Company</th>
          <th className="text-center">Role</th>
          <th className="text-center">Access</th>
          <th className="users-cell-actions">Actions</th>
        </tr>
      </thead>
    );
  }

  renderUsersTable(rows) {
    return (
      <div className="users-table-wrap">
        <Table className="users-table align-middle">
          {this.renderTableHeader()}
          <tbody>{rows.map((user) => this.renderUserRow(user))}</tbody>
        </Table>
      </div>
    );
  }

  renderSkeletonRows() {
    return (
      <div className="users-table-wrap">
        <Table className="users-table align-middle">
          {this.renderTableHeader()}
          <tbody>
            {Array.from({ length: 8 }).map((_, index) => (
              <tr key={index} className="users-row users-row--skeleton">
                <td>
                  <span className="sk sk-line sk-line--lg" />
                  <span className="sk sk-line sk-line--sm" />
                </td>
                <td>
                  <span className="sk sk-line" />
                </td>
                <td className="text-center">
                  <span className="sk sk-pill" />
                </td>
                <td className="text-center">
                  <span className="sk sk-pill" />
                </td>
                <td className="users-cell-actions">
                  <span className="sk sk-dot" />
                  <span className="sk sk-dot" />
                  <span className="sk sk-dot" />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  render() {
    const { users, total, page, filters, companies, refreshing } = this.props;
    const totalPages = Math.max(1, Math.ceil(total / USERS_PER_PAGE));
    // Ids are stringified so the filter keeps sending what <option> used to.
    const companyOptions = companies.all.map((company) => ({
      value: String(company.id),
      label: company.name,
    }));
    return (
      <>
        <UserDrawer
          alert={this.props.userUpdateAlert}
          isOpen={this.state.isOpenedModal || this.props.userUpdateAlert !== ""}
          onClose={() => {
            this.cancelModal();
          }}
          onSubmit={(user) => {
            this.props.onUpdateUser(user);
            this.setState({ isOpenedModal: false });
          }}
          user={this.state.updatedUser}
          companies={this.props.companies}
        />

        <div className="page-content container-fluid cf-page">
          <LevelHeader path={[{ label: "Users" }]} />
          <div className="cf-filters">
            <div className="cf-search">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              <input
                type="search"
                placeholder="Search a user, a campaign or site id..."
                value={this.state.search}
                onChange={this.handleFilterChange}
              />
            </div>

            <Select
              className="cf-rs"
              classNamePrefix="cf-rs"
              isClearable
              options={companyOptions}
              value={
                companyOptions.find(
                  (option) => option.value === (filters.company_id ?? "")
                ) ?? null
              }
              placeholder="All companies"
              onChange={(option) =>
                this.handleFilterSelect("company_id", option?.value ?? "")
              }
              aria-label="Filter by company"
            />

            <Select
              className="cf-rs"
              classNamePrefix="cf-rs"
              isSearchable={false}
              isClearable
              options={ACCESS_OPTIONS}
              value={
                ACCESS_OPTIONS.find(
                  (option) => option.value === (filters.access ?? "")
                ) ?? null
              }
              placeholder="All users"
              onChange={(option) =>
                this.handleFilterSelect("access", option?.value ?? "")
              }
              aria-label="Filter by access"
            />

            <Select
              className="cf-rs"
              classNamePrefix="cf-rs"
              isSearchable={false}
              isClearable
              options={SORT_OPTIONS}
              value={
                SORT_OPTIONS.find(
                  (option) => option.value === (this.props.sort ?? "")
                ) ?? null
              }
              placeholder="Sort: Name"
              onChange={this.handleSortChange}
              aria-label="Sort users"
            />

            <div className="cf-filters__spacer" />
            <div className="users-toolbar__actions">
              <Button
                color="primary"
                className="users-newbtn"
                onClick={this.handleCreateUser}
              >
                <i className="fa-solid fa-plus me-1" /> New user
              </Button>
              <RefreshButton
                refreshing={refreshing}
                onRefresh={() => this.props.handleRefresh()}
                className="users-refresh"
                data-tooltip-id="tooltip"
                data-tooltip-content="Refresh"
              />
            </div>
          </div>

          {refreshing && this.renderSkeletonRows()}
          {!refreshing && total === 0 && (
            <EmptyState icon="fa-solid fa-user-slash" title="No users found." />
          )}
          {!refreshing && total > 0 && this.renderUsersTable(users)}
          {!refreshing && total > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <span className="text-muted">{total} users</span>
              <div className="d-flex align-items-center gap-2">
                <Button
                  size="sm"
                  color="link"
                  disabled={page <= 1}
                  onClick={() => this.goToPage(page - 1)}
                >
                  <i className="fa-solid fa-chevron-left" /> Previous
                </Button>
                <span>
                  Page {page} / {totalPages}
                </span>
                <Button
                  size="sm"
                  color="link"
                  disabled={page >= totalPages}
                  onClick={() => this.goToPage(page + 1)}
                >
                  Next <i className="fa-solid fa-chevron-right" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }
}

function mapStateToProps(state) {
  return {
    currentUser: state.user,
    users: state.users.all,
    total: state.users.total,
    page: state.users.page,
    sort: state.users.sort,
    filters: state.users.filters,
    userUpdateAlert: state.users.updateAlert,
    refreshing: state.users.table.refreshing,
    companies: state.companies,
  };
}

const callbacks = {
  changeAccess,
  resetUserAlert,
  handleRefresh: fetchUsers,
  onDeleteUser: deleteUser,
  onUpdateUser: updateUserInformation,
  fetchCompanies,
};

export default connect(mapStateToProps, callbacks)(Users);

Users.propTypes = {
  currentUser: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  sort: PropTypes.string.isRequired,
  filters: PropTypes.object.isRequired,
  refreshing: PropTypes.bool.isRequired,
  userUpdateAlert: PropTypes.string.isRequired,
  changeAccess: PropTypes.func.isRequired,
  resetUserAlert: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func.isRequired,
  onDeleteUser: PropTypes.func.isRequired,
  onUpdateUser: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  companies: PropTypes.object.isRequired,
  fetchCompanies: PropTypes.func.isRequired,
};
